'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APP_WHOOP_GIFT_EXTENSIONS_URL,
  cltw,
  createWYWImagesObject,
  getFirstVariant,
  getMembershipVariantV2,
  hasMembershipKey,
} from 'ui';
import { getCheckoutRegionFromRegion } from '@whoop/i18n/utils/i18nHelpers';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { useTranslation } from '@whoop/i18n/lang/client';
import { getFormatters } from 'ui/hooks/formatter';
import type { SelectedVariantMap } from 'ui/utils/variantHelpers';
import {
  getFamilySizeFromVariant,
  getFirstImageForVariant,
  getPriceValues,
  isWywVariant,
} from 'ui/utils/variantHelpers';
import { useShallow } from 'zustand/react/shallow';
import {
  ANNUAL_MEMBERSHIP_KEY,
  TRIAL_MEMBERSHIP_KEY,
} from 'ui/utils/constants/membershipConstants';
import type { JoinFlowContent } from 'services/generated/growth-content-service/models/JoinFlowContent';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useToast } from 'ui/components/Toast/use-toast';
import { FAMILY_MEMBERSHIP_KEY } from 'ui/utils/constants/familyConstants';
import { getTrialDepositValueFromMemberships } from 'app/[country]/[language]/membership/utils/trialDepositHelpers';
import HeroSection from '../HeroImages/HeroImages';
import LandingPageHeader from '../LandingPageHeader/LandingPageHeader';
import LandingPageSubtext from '../landingPageSubtext/landingPageSubtext';
import {
  getLandingPageSubtextCopy,
  getSiteWidePromoCopy,
  sortAndFilterLandingSwatches,
} from '../LandingPageContent/landingPageContentUtils';
import BandSelector from '../BandSelector/BandSelector';
import LandingPageBottomCTA from '../LandingPageBottomCTA/LandingPageBottomCTA';
import LandingPageModals from '../LandingPageModals/LandingPageModals';
import { HsaFsaBadge } from '@/components/HsaFsaBadge/HsaFsaBadge';
import { featureFlagNames } from '@/client/featureFlagClient';
import { GIFT } from '@/lib/constants/memberships';
import { useNavigation } from '@/hooks/navigation';
import { useSiteStore } from '@/store/SiteStoreProvider';
import {
  getItemProperties,
  getMembershipProperties,
  getNameFromVariant,
} from '@/lib/utils/analyticsHelper';
import { addToEcommCart } from '@/actions/cartActions';
import { getMembershipFromQueryParam } from '@/lib/utils/skipMembershipSelectHelper';
import { useAnalytics } from '@/hooks/analytics';

interface Props {
  skipMemSelection: string | undefined;
  isMobile: boolean;
  skipMemFamilySize: string | undefined;
  propPromoContent: JoinFlowContent | undefined;
  gccTrialDepositExperimentVariant: string;
}

const HeroSectionWrapper: React.FC<Props> = ({
  skipMemSelection,
  isMobile,
  skipMemFamilySize,
  propPromoContent,
  gccTrialDepositExperimentVariant,
}) => {
  const {
    currency,
    language,
    region,
    country,
    storePromoContent,
    promoInfo,
    isRafGiftFlow,
    siteWidePromoContent,
    featureFlags,
    cart,
    setEcommCart,
    setAsAGift,
    setIsCartOpen,
    ecommCatalog,
  } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      language: state.language,
      region: state.region,
      country: state.country,
      storePromoContent: state.promoContent,
      promoInfo: state.promoInfo,
      isRafGiftFlow: state.isRafGiftFlow,
      cart: state.cart,
      setEcommCart: state.setEcommCart,
      setAsAGift: state.setAsAGift,
      setIsCartOpen: state.setIsCartOpen,
      featureFlags: state.featureFlags,
      siteWidePromoContent: state.siteWidePromoContent,
      ecommCatalog: state.ecommCatalog,
    })),
  );
  const { toast } = useToast();

  const { t } = useTranslation('joinLandingPage', language);
  const { goTo } = useNavigation();
  const { trackAmplitudeEvent } = useAnalytics();
  const { t: cartT } = useTranslation('cart', language);

  const skipGiftSelection = skipMemSelection === GIFT;

  const [isLoading, setIsLoading] = useState(false);

  const swatchProducts = useMemo(() => {
    return sortAndFilterLandingSwatches(ecommCatalog.initial_products);
  }, [ecommCatalog]);

  const [selectedVariant, setSelectedVariant] = useState<Variant>();
  const [hoveredVariant, setHoveredVariant] = useState<Variant | undefined>();

  useEffect(() => {
    setSelectedVariant(getFirstVariant(swatchProducts[0]));
  }, [swatchProducts]);

  const [src, setSrc] = useState(
    `/recovery/${language}/recovery-overview.webp`,
  );

  const [showEngravingModal, setShowEngravingModal] = useState(false);
  const [selectedWywVariants, setSelectedWywVariants] =
    useState<SelectedVariantMap>();
  const [showWywModal, setShowWywModal] = useState(false);

  const israelDisabled = featureFlags[featureFlagNames.DISABLE_IL_JF];
  const inIsraelAndDisabled = region === 'IL' && israelDisabled;
  const isJapanDisabled = featureFlags[featureFlagNames.DISABLE_JP_JF];
  const inJapanAndDisabled = region === 'JP' && isJapanDisabled;

  const isDisabledDueToShippingCountry =
    inIsraelAndDisabled || inJapanAndDisabled;

  const { formatDisplayPrice } = getFormatters({ currency, language, region });
  const trialDepositValueInCents = getTrialDepositValueFromMemberships(
    ecommCatalog.memberships,
  );
  const trialDepositValue = formatDisplayPrice(trialDepositValueInCents);

  const { promoContent } = useMemo(() => {
    const promoContent = storePromoContent
      ? storePromoContent
      : propPromoContent;
    return {
      promoContent,
    };
  }, [storePromoContent, propPromoContent]);

  const isJfHeroCarouselEnabled = Boolean(isMobile && !promoInfo?.affiliate);

  const wywParentCategory =
    ecommCatalog.accessories.find(
      (category) => category.key === 'whoop-your-way-kit',
    ) ?? null;

  const wywVariant = wywParentCategory?.products[0]
    ? getFirstVariant(wywParentCategory.products[0])
    : null;

  const deviceImage = useMemo(() => {
    const variant = hoveredVariant ?? selectedVariant;
    if (!variant) return;
    if (isWywVariant(variant)) {
      return '/wyw_flash.gif';
    }

    return getFirstImageForVariant(variant);
  }, [hoveredVariant, selectedVariant]);

  const wywImagesObject = useMemo(() => {
    if (wywVariant && selectedWywVariants) {
      return createWYWImagesObject(wywVariant, selectedWywVariants).front;
    }
  }, [wywVariant, selectedWywVariants]);

  const wywImages = useMemo(() => {
    const variant = hoveredVariant ?? selectedVariant;
    if (variant && isWywVariant(variant)) {
      return wywImagesObject;
    }
  }, [hoveredVariant, selectedVariant, wywImagesObject]);

  const onSwatchHover = useCallback((variant: Variant | undefined): void => {
    setHoveredVariant(variant);
  }, []);

  const onSwatchClick = useCallback((variant: Variant) => {
    if (isWywVariant(variant)) {
      setShowWywModal(true);
    } else {
      setSelectedWywVariants(undefined);
    }
    setHoveredVariant(variant);
    setSelectedVariant(variant);
  }, []);

  const productWithDiscount = ecommCatalog.memberships.find((mem) => {
    return mem.variants.find((variant) => {
      return Boolean(variant.price?.discounted);
    });
  })?.variants[0];

  const hasTrialMembership = hasMembershipKey(
    TRIAL_MEMBERSHIP_KEY,
    ecommCatalog.memberships,
  );

  const annualMembership = getMembershipVariantV2(
    ANNUAL_MEMBERSHIP_KEY,
    ecommCatalog.memberships,
  );
  const annualPrice = annualMembership
    ? formatDisplayPrice(getPriceValues(annualMembership).displayPrice)
    : undefined;

  const handleContinue = async (
    destination: string,
    continueWithoutBundle = false,
  ): Promise<void> => {
    // To avoid duplication, use default variant if wyw is selected
    // (wyw is added in the modal)
    if (!selectedVariant) return;
    const variantToAdd =
      isWywVariant(selectedVariant) || continueWithoutBundle
        ? getFirstVariant(swatchProducts[0])
        : selectedVariant;

    const productName = getNameFromVariant(
      ecommCatalog.initial_products,
      variantToAdd,
    );

    trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
      ...getItemProperties(productName, variantToAdd),
      action: 'click',
    });

    try {
      let newCart = cart;

      newCart = await addToEcommCart({
        requestBody: {
          cart_id: cart.id,
          country,
          locale: language,
          sku: variantToAdd.sku,
          quantity: 1,
        },
      });

      setEcommCart(newCart);
      trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
        ...getItemProperties(productName, variantToAdd),
        action: 'success',
      });
      goTo(destination);
    } catch (error) {
      setIsLoading(false);
      toast({
        title: cartT('errorTitle'),
        description: cartT('errorDescription'),
        variant: 'error',
      });
    }
  };

  const handleJoinNow = async (
    destination: string,
    continueWithoutBundle = false,
  ): Promise<void> => {
    setIsLoading(true);
    setAsAGift(false);
    await handleContinue(destination, continueWithoutBundle);
  };

  const checkSkipMembership = async (): Promise<void> => {
    if (!selectedVariant) return;
    try {
      setIsLoading(true);
      const membership = getMembershipFromQueryParam(
        skipMemSelection ?? '',
        ecommCatalog.memberships,
        skipMemFamilySize,
      );
      if (skipMemSelection && membership) {
        // To avoid duplication, use default variant if wyw is selected
        // (wyw is added in the modal)
        const variantToAdd = isWywVariant(selectedVariant)
          ? getFirstVariant(swatchProducts[0])
          : selectedVariant;

        const productName = getNameFromVariant(
          ecommCatalog.initial_products,
          variantToAdd,
        );

        trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
          ...getItemProperties(productName, variantToAdd),
          action: 'click',
        });
        setIsLoading(true);
        setAsAGift(skipGiftSelection);

        // Adding the selected variant band variant to the cart if it exists
        let newCart = await addToEcommCart({
          requestBody: {
            cart_id: cart.id,
            country,
            locale: language,
            sku: variantToAdd.sku,
            quantity: 1,
          },
        });
        trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
          ...getItemProperties(productName, variantToAdd),
          action: 'success',
        });
        const firstVariant = membership.variants[0];
        let skipMemVariant: Variant = firstVariant;
        const isFamilyMembership = membership.key === FAMILY_MEMBERSHIP_KEY;
        if (isFamilyMembership) {
          skipMemVariant =
            membership?.variants.find(
              (variant: Variant) =>
                parseInt(skipMemFamilySize || '0') ===
                getFamilySizeFromVariant(variant),
            ) || firstVariant;
        }
        trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
          ...getMembershipProperties(skipMemVariant, language),
          family_size: skipMemFamilySize,
          action: 'click',
        });

        newCart = await addToEcommCart({
          requestBody: {
            cart_id: newCart.id,
            country,
            locale: language,
            sku: skipMemVariant.sku,
            quantity: 1,
          },
        });
        trackAmplitudeEvent(AmplitudeEvents.AddedItemToCart, {
          ...getMembershipProperties(skipMemVariant, language),
          family_size: skipMemFamilySize,
          action: 'success',
        });
        setEcommCart(newCart);
        setIsCartOpen(true);
        setIsLoading(false);
      } else {
        await handleJoinNow('membership');
      }
    } catch (error) {
      toast({
        title: cartT('errorTitle'),
        description: cartT('errorDescription'),
        variant: 'error',
      });
    }
  };

  let landingPageSubtextCopy =
    siteWidePromoContent &&
    productWithDiscount &&
    promoInfo &&
    getSiteWidePromoCopy({
      currency,
      product: productWithDiscount,
      siteWidePromoContent,
      promoInfo,
    });

  landingPageSubtextCopy ||= getLandingPageSubtextCopy({
    isOnGCCTrialDepositExperiment:
      gccTrialDepositExperimentVariant === 'experiment',
    trialDepositValue,
    hasTrialMembership,
    promoContent,
    promoInfo,
    currency,
    language,
    region,
    isRafGiftFlow,
    skipMemSelection,
    skipMemFamilySize,
    annualPrice,
  });

  return (
    <>
      <LandingPageModals
        setSelectedVariant={setSelectedVariant}
        setShowEngravingModal={setShowEngravingModal}
        setShowWywModal={setShowWywModal}
        showEngravingModal={showEngravingModal}
        showWywModal={showWywModal}
        skipMemSelection={skipMemSelection}
        swatchProducts={swatchProducts}
      />
      <section
        className={cltw(
          'order-1 grid h-full min-h-[850px] w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-2',
          isJfHeroCarouselEnabled ? 'bg-white' : 'bg-gray-50',
        )}
      >
        <div
          className={cltw(
            'hidden justify-end self-start pl-8 md:flex',
            isJfHeroCarouselEnabled ? 'pt-14' : 'pt-20',
          )}
        >
          {selectedVariant ? (
            <HeroSection
              deviceImage={deviceImage ?? ''}
              hoveredVariant={hoveredVariant}
              isJfHeroCarouselEnabled={isJfHeroCarouselEnabled}
              language={language}
              region={region}
              selectedVariant={selectedVariant}
              setSrc={setSrc}
              src={src}
              wywImages={wywImages}
            />
          ) : null}
        </div>
        <div className='w-full flex-grow basis-0 px-4 py-5 md:py-12'>
          <div className='m-auto flex w-full max-w-[500px] flex-col justify-center text-center sm:pt-5 md:m-0 md:pl-16 md:text-left'>
            <LandingPageHeader skipGiftSelection={skipGiftSelection} />
            <LandingPageSubtext
              desktopSubtext={landingPageSubtextCopy.desktopSubtext}
              imageSrc={landingPageSubtextCopy.imageSrc}
              mobileSubtext={landingPageSubtextCopy.mobileSubtext}
              secondLine={landingPageSubtextCopy.secondLine}
            />
            <div className='flex items-center justify-center md:hidden md:px-0'>
              {selectedVariant ? (
                <HeroSection
                  deviceImage={deviceImage ?? ''}
                  hoveredVariant={hoveredVariant}
                  isJfHeroCarouselEnabled={isJfHeroCarouselEnabled}
                  language={language}
                  region={region}
                  selectedVariant={selectedVariant}
                  setSrc={setSrc}
                  src={src}
                  wywImages={wywImages}
                />
              ) : null}
            </div>
            {/* Personalize section */}
            {selectedVariant ? (
              <BandSelector
                checkSkipMembership={checkSkipMembership}
                handleContinue={handleContinue}
                handleJoinNow={handleJoinNow}
                hoveredVariant={hoveredVariant}
                isLoading={isLoading}
                isMobile={isMobile}
                onSwatchClick={onSwatchClick}
                onSwatchHover={onSwatchHover}
                selectedVariant={selectedVariant}
                setIsLoading={setIsLoading}
                setShowEngravingModal={setShowEngravingModal}
                showEngravingModal={showEngravingModal}
                skipMemSelection={skipMemSelection}
                swatchProducts={swatchProducts}
              />
            ) : null}
            <HsaFsaBadge country={country} language={language} />
            {skipGiftSelection ? (
              <div className='flex w-full flex-col justify-center gap-2 pt-4 md:flex-row md:gap-3'>
                <div className='text-sm tracking-wider'>
                  {t('giftingCurrent')}
                </div>
                <a
                  className='text-accent ml-0 border-none bg-none p-0 text-sm tracking-wider underline md:ml-1'
                  href={`${APP_WHOOP_GIFT_EXTENSIONS_URL}?region=${getCheckoutRegionFromRegion(
                    region,
                  )}`}
                  rel='noreferer noopener'
                  target='_self'
                  type='button'
                >
                  {t('extendMembership')}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </section>
      {!isDisabledDueToShippingCountry && (
        <LandingPageBottomCTA
          disabled={isLoading}
          onClick={checkSkipMembership}
          skipGiftSelection={skipGiftSelection}
        />
      )}
    </>
  );
};

export default HeroSectionWrapper;
